import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { neueAktieApi } from 'modules/aktien/features/aktienApi';
import {
  fetchAllAktienUebersicht,
  selectAktie,
} from 'modules/aktien/features/aktienDbSlice';

/**
 * Reiter „Aktie hinzufügen": eine oder viele ISINs/WKNs (eine pro Zeile oder
 * durch Komma/Leerzeichen getrennt). Jede Kennung wird nacheinander über OnVista
 * vollständig eingerichtet (Stammdaten, Kurs, Jahreswerte, Dividenden, komplette
 * Kurshistorie). Sequenziell wegen des OnVista-Rate-Limits; ein Fehler bricht die
 * übrigen nicht ab.
 */
export default function AktieHinzufuegenView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [eingabe, setEingabe] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnisse, setErgebnisse] = useState([]);

  const kennungen = useMemo(() => parseKennungen(eingabe), [eingabe]);
  const fertig = ergebnisse.filter((e) => istAbgeschlossen(e.status)).length;

  // Aktualisiert einen Ergebnis-Eintrag anhand seines Index.
  const setEintrag = (index, patch) =>
    setErgebnisse((prev) =>
      prev.map((e, i) => (i === index ? { ...e, ...patch } : e))
    );

  const absenden = async (e) => {
    e.preventDefault();
    if (kennungen.length === 0 || laeuft) {
      return;
    }
    setLaeuft(true);
    setErgebnisse(kennungen.map((k) => ({ kennung: k, status: 'wartet' })));
    for (let i = 0; i < kennungen.length; i++) {
      setEintrag(i, { status: 'laedt' });
      setEintrag(i, await verarbeiteKennung(kennungen[i]));
    }
    setLaeuft(false);
    setEingabe('');
    dispatch(fetchAllAktienUebersicht()); // Liste DB aktualisieren
  };

  const zurDetailseite = (isin) => {
    if (!isin) {
      return;
    }
    dispatch(selectAktie(isin));
    navigate(`/projekte/aktienanalyse/aktie/${isin}`);
  };

  return (
    <div>
      <header className="mb-6">
        <h1 className="font-serif text-3xl font-semibold text-ink">
          Aktien hinzufügen
        </h1>
        <p className="mt-1 text-sm text-ink-muted">
          Eine oder mehrere ISINs/WKNs eingeben – eine pro Zeile oder durch
          Komma getrennt. Jede Aktie wird automatisch über OnVista eingerichtet
          und mit voller Kurshistorie in die Datenbank aufgenommen.
        </p>
      </header>

      <form onSubmit={absenden} className="max-w-md">
        <label
          htmlFor="kennungen"
          className="mb-1 block text-sm font-medium text-ink"
        >
          ISINs oder WKNs
        </label>
        <textarea
          id="kennungen"
          rows={6}
          value={eingabe}
          onChange={(ev) => setEingabe(ev.target.value)}
          placeholder={'US0378331005\n865985\nDE0007164600'}
          disabled={laeuft}
          className="w-full resize-y rounded-lg border border-line bg-paper-soft px-3 py-2 font-mono text-sm text-ink placeholder-ink-muted outline-none transition-colors focus:border-ink/30 focus:ring-1 focus:ring-ink/20 disabled:opacity-60"
        />
        <div className="mt-2 flex items-center gap-3">
          <button
            type="submit"
            disabled={laeuft || kennungen.length === 0}
            className="rounded-lg bg-ink px-4 py-2 text-sm font-medium text-paper transition-colors hover:bg-ink/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {laeuft
              ? `Richte ein… ${fertig}/${ergebnisse.length}`
              : hinzufuegenLabel(kennungen.length)}
          </button>
          {kennungen.length > 0 && !laeuft && (
            <span className="font-mono text-xs text-ink-muted">
              {kennungen.length} Kennung{kennungen.length === 1 ? '' : 'en'}
            </span>
          )}
        </div>
      </form>

      {laeuft && (
        <p className="mt-4 max-w-md text-sm text-ink-muted">
          OnVista wird je Aktie abgefragt und die komplette Kurshistorie geladen.
          Das dauert wegen des Abfragelimits einige Sekunden pro Aktie…
        </p>
      )}

      {ergebnisse.length > 0 && (
        <ErgebnisListe ergebnisse={ergebnisse} onDetail={zurDetailseite} />
      )}
    </div>
  );
}

/** Zeigt die Zusammenfassung und den Status je eingegebener Kennung. */
function ErgebnisListe({ ergebnisse, onDetail }) {
  const zaehler = zaehleStatus(ergebnisse);
  return (
    <div className="mt-6 max-w-md">
      <p className="mb-2 font-mono text-xs text-ink-muted">
        {zaehler.ok} eingerichtet · {zaehler.vorhanden} bereits vorhanden ·{' '}
        {zaehler.fehler} fehlgeschlagen
      </p>
      <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-paper-soft">
        {ergebnisse.map((e) => (
          <li
            key={e.kennung}
            className="flex items-center gap-3 px-4 py-2.5 text-sm"
          >
            <span className="w-5 shrink-0 text-center">
              {statusIcon(e.status)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-ink">
                {e.name || e.kennung}
              </div>
              {e.meldung && (
                <div className="truncate text-xs text-ink-muted">
                  {e.meldung}
                </div>
              )}
            </div>
            {e.status === 'ok' && e.isin && (
              <button
                type="button"
                onClick={() => onDetail(e.isin)}
                className="shrink-0 rounded-md border border-line bg-paper px-2 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
              >
                📊 Detail
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Richtet eine einzelne Kennung ein und liefert deren Ergebnis-Eintrag. */
async function verarbeiteKennung(kennung) {
  try {
    const aktie = await neueAktieApi(kennung);
    return {
      status: 'ok',
      name: aktie.aktienName,
      isin: aktie.isin,
      wkn: aktie.wkn,
    };
  } catch (err) {
    if (err.response?.status === 409) {
      return { status: 'vorhanden', meldung: 'Bereits in der Datenbank' };
    }
    return { status: 'fehler', meldung: fehlermeldung(err) };
  }
}

/** Zerlegt die Eingabe in eindeutige, großgeschriebene Kennungen. */
function parseKennungen(text) {
  const roh = text
    .split(/[\s,;]+/)
    .map((k) => k.trim().toUpperCase())
    .filter(Boolean);
  return [...new Set(roh)];
}

/** Beschriftung des Buttons abhängig von der Anzahl Kennungen. */
function hinzufuegenLabel(anzahl) {
  if (anzahl <= 1) {
    return 'Hinzufügen';
  }
  return `${anzahl} hinzufügen`;
}

/** Ein Eintrag ist abgeschlossen, sobald er nicht mehr wartet oder lädt. */
function istAbgeschlossen(status) {
  return status !== 'wartet' && status !== 'laedt';
}

/** Zählt die Ergebnisse nach Endstatus. */
function zaehleStatus(ergebnisse) {
  return {
    ok: ergebnisse.filter((e) => e.status === 'ok').length,
    vorhanden: ergebnisse.filter((e) => e.status === 'vorhanden').length,
    fehler: ergebnisse.filter((e) => e.status === 'fehler').length,
  };
}

/** Statussymbol je Ergebnis-Zustand. */
function statusIcon(status) {
  const icons = {
    wartet: '⏳',
    laedt: '⏳',
    ok: '✅',
    vorhanden: '⏭️',
    fehler: '❌',
  };
  return icons[status] || '·';
}

/** Wandelt einen Fehler aus dem API-Aufruf in eine verständliche Meldung um. */
function fehlermeldung(err) {
  const code = err.response?.status;
  const backend = err.response?.data?.message;
  if (code === 404) {
    return backend || 'Bei OnVista keine Aktie zu dieser ISIN/WKN gefunden.';
  }
  if (code === 503) {
    return backend || 'OnVista ist gerade nicht erreichbar.';
  }
  if (err.code === 'ECONNABORTED') {
    return 'Zeitüberschreitung – Liste DB prüfen, ob die Aktie dennoch angelegt wurde.';
  }
  return backend || 'Die Aktie konnte nicht hinzugefügt werden.';
}
